"use client";

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Icon, type IconName } from "@/lib/icons/Icon";
import { Field, FileUpload, FormStatus, Input, Select, SubmitButton, Textarea } from "@/components/ui/Form";
import { toast } from "@/lib/notifications/toast";
import {
  createProjectTask,
  fetchTaskFormOptions,
  TaskApiError,
  TASK_TYPE_SUPPORT,
  TASK_TYPE_TASK,
  TASK_TYPE_WARRANTY,
  type TaskFormOptions,
  type TaskTypeOption,
  type TaskTypeReason,
  type UserProject,
} from "@/lib/api/c-crm-project-management-user";
import styles from "./CCrmProjectManagement.module.css";

/** Limit zalacznikow - musi zgadzac sie z `MAX_TASK_FILES` w komponencie PHP,
    ktory sprawdza go ponownie po stronie serwera. */
const MAX_TASK_FILES = 10;

/**
 * Formularz zgłoszenia zadania w projekcie (plugin c_crm_project_management).
 *
 * **Kreator dwukrokowy** - wybór typu jest osobną decyzją i dostaje własny ekran:
 *   1. `type` - kafelki typów zgłoszenia (ikona, nazwa, opis, powód niedostępności),
 *   2. `form` - dane zgłoszenia: nazwa, opis, a przy typie wymagającym usługi
 *      także wskazanie konkretnej usługi projektu.
 *
 * Rozdzielenie kroków jest celowe: typ determinuje resztę formularza (Wsparcie
 * wymusza wybór usługi) i decyduje o tym, czy zgłoszenie w ogóle jest możliwe.
 * W jednym ekranie klient widziałby pola, których wypełnienie i tak nic nie da.
 *
 * **Front NIE decyduje o dostępności typów.** Reguły (pakiety uprawniające, pakiety
 * gwarancyjne, wymagana aktywna usługa) żyją w PHP
 * (`C_CRM_PROJECT_MANAGEMENT_CONTROLLER::resolveTaskTypes`) i są powtórzone przy
 * zapisie - tutaj są wyłącznie prezentowane. Typy niedostępne pokazujemy WYŁĄCZONE
 * z powodem, zamiast je ukrywać: klient ma wiedzieć, czego mu brakuje.
 *
 * **Przydzielenie:** zgłoszenie trafia zawsze do menadżera projektu - klient go nie
 * wybiera. Gdy projekt nie ma przydzielonego PM-a, backend blokuje dodawanie
 * (`can_add = false`) i wysyła pilne powiadomienie do administracji; kreator
 * pokazuje wtedy komunikat o kontakcie zamiast kroków.
 */

const schema = z.object({
  /* Trzymane jako string, bo tak wartość wraca z kontrolek formularza - konwersja
     na liczbę dzieje się dopiero przy budowie payloadu. `z.coerce.number()` dawałoby
     rozjazd typu wejściowego i wyjściowego schematu, którego resolver RHF nie przyjmuje. */
  id_type: z.string().min(1, "Wybierz typ zgłoszenia."),
  name: z.string().trim().min(1, "Podaj nazwę zadania.").max(500, "Nazwa może mieć maksymalnie 500 znaków."),
  description: z
    .string()
    .trim()
    .min(1, "Opisz czego dotyczy zgłoszenie.")
    .max(20000, "Opis jest zbyt długi."),
  /* Pusty string = brak wyboru w selekcie. Wymagalność zależy od typu, więc jest
     sprawdzana tuż przed wysyłką, a nie na poziomie samego pola. */
  id_project_service: z.string().optional(),
  /* Priorytet - pole pojawia sie tylko przy usludze priorytetowej, wiec pusty
     string jest normalnym stanem i nie moze blokowac wysylki. */
  id_priority: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/**
 * Prezentacja kafelka per typ zgłoszenia. Nazwa typu przychodzi z backendu
 * (słownik modułu Zadania), a ikona i opis są warstwą czysto interfejsową -
 * tłumaczą klientowi, KIEDY wybrać dany typ.
 */
const TYPE_PRESENTATION: Record<number, { icon: IconName; description: string }> = {
  [TASK_TYPE_TASK]: {
    icon: "list-checks",
    description:
      "Nowa praca do wykonania w projekcie - zmiana, rozbudowa albo dodanie funkcji.",
  },
  [TASK_TYPE_SUPPORT]: {
    icon: "gear",
    description:
      "Pomoc przy działającej usłudze - konfiguracja, pytanie albo problem z bieżącym utrzymaniem. Wskażesz, której usługi dotyczy.",
  },
  [TASK_TYPE_WARRANTY]: {
    icon: "shield-check",
    description:
      "Naprawa w ramach gwarancji - coś, co wcześniej działało poprawnie, przestało działać. Wskażesz usługę gwarancyjną, której dotyczy zgłoszenie.",
  },
};

/**
 * Opisy priorytetów - warstwa czysto interfejsowa. Nazwy przychodzą ze słownika
 * modułu Zadania, a zdanie obok tłumaczy, co dany wybór oznacza w praktyce, żeby
 * klient nie musiał zgadywać różnicy między "Wysoki" a "Pilny".
 *
 * Klucze to stałe `M_CRM_TASKS_PRIORITY_*` z define.php (HIGH=3, INSTANT=4, URGENT=5).
 * Kolejność wyświetlania bierzemy Z BACKENDU - słownik jest tam ułożony wg rosnącej
 * wagi (1, 2, 3, 5, 4), więc sortowanie po id zepsułoby ten porządek.
 */
const PRIORITY_PRESENTATION: Record<number, { icon: IconName; description: string }> = {
  1: { icon: "caret-down", description: "Może poczekać - zrobimy w normalnej kolejce." },
  2: { icon: "info", description: "Standardowa kolejność realizacji." },
  3: { icon: "caret-up", description: "Ważne - przed innymi bieżącymi zadaniami." },
  5: { icon: "warning", description: "Sprawa pilna, wymaga szybkiej reakcji." },
  4: { icon: "warning-circle", description: "Blokuje pracę lub awaria - reagujemy natychmiast." },
};

/** Komunikat "dlaczego ten typ jest niedostępny" - mapa powodów z backendu. */
const REASON_TEXT: Record<TaskTypeReason, string> = {
  requires_package:
    "Zgłaszanie zadań w tym projekcie wymaga aktywnej usługi z pakietu obsługi.",
  no_active_service: "Projekt nie ma aktywnej usługi, której mogłoby dotyczyć wsparcie.",
  no_support_service: "Projekt nie ma aktywnej usługi objętej wsparciem.",
  no_warranty_service: "Projekt nie ma aktywnej usługi gwarancyjnej.",
  warranty_not_configured: "Zgłoszenia gwarancyjne nie są dostępne w tym serwisie.",
  no_services_module: "Obsługa usług nie jest dostępna w tym serwisie.",
};

/**
 * Mapowanie kodu błędu Imperiala na pole formularza. Dzięki temu komunikat
 * z serwera ląduje przy właściwej kontrolce, a nie tylko w pasku na górze.
 */
const ERROR_FIELD: Record<number, keyof FormValues> = {
  2260024: "name",
  2260025: "id_type",
  2260026: "id_type",
  2260028: "id_project_service",
  2260029: "id_project_service",
};

export function TaskForm({
  project,
  onSaved,
  onCancel,
}: {
  project: Pick<UserProject, "id" | "nr" | "name">;
  /** Wywoływane po udanym zgłoszeniu - panel wraca wtedy na listę zadań. */
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [options, setOptions] = useState<TaskFormOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  //Zalaczniki trzymane poza react-hook-form - <input type="file"> nie jest polem kontrolowanym.
  const [files, setFiles] = useState<File[]>([]);
  //Krok kreatora. Zawsze startujemy od wyboru typu - to on determinuje resztę pól.
  const [step, setStep] = useState<"type" | "form">("type");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      id_type: "",
      name: "",
      description: "",
      id_project_service: "",
      id_priority: "",
    },
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const data = await fetchTaskFormOptions(project.id);
        if (cancelled) return;
        setOptions(data);
        /* Preselekcja "Normalny" (2) gdy klient moze wybierac priorytet - inaczej
           kafelki startowalyby bez zaznaczenia, a zadanie i tak dostaloby ten sam
           priorytet domyslnie. Gdy slownik go nie ma, zostawiamy pusty wybor. */
        if (data.can_set_priority && data.priorities.some((p) => p.id === 2))
          setValue("id_priority", "2");
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.id, setValue]);

  const selectedTypeId = Number(watch("id_type"));
  const selectedType: TaskTypeOption | undefined = useMemo(
    () => options?.types.find((t) => t.id === selectedTypeId),
    [options, selectedTypeId],
  );

  const needsService = !!selectedType?.requires_service;
  const hasAnyAvailableType = !!options?.types.some((t) => t.available);

  /** Wybór kafelka - zapisuje typ i przechodzi do kroku z danymi zgłoszenia. */
  function chooseType(type: TaskTypeOption) {
    if (!type.available) return;
    setValue("id_type", String(type.id), { shouldValidate: true });
    //Usługa należy do poprzedniego wyboru - przy zmianie typu traci sens.
    setValue("id_project_service", "");
    setSubmitError(null);
    setStep("form");
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    //Wymagalność usługi zależy od typu, więc sprawdzamy ją tuż przed wysyłką.
    if (needsService && !values.id_project_service) {
      setError("id_project_service", { type: "manual", message: "Wskaż usługę, której dotyczy zgłoszenie." });
      return;
    }

    try {
      const res = await createProjectTask({
        id_project: project.id,
        id_type: Number(values.id_type),
        name: values.name,
        description: values.description || undefined,
        id_project_service:
          needsService && values.id_project_service ? Number(values.id_project_service) : undefined,
        id_priority:
          options?.can_set_priority && values.id_priority ? Number(values.id_priority) : undefined,
        files,
      });
      toast.success(`Zgłoszenie ${res.task.nr} zostało przekazane do realizacji.`);
      onSaved();
    } catch (e) {
      if (e instanceof TaskApiError && ERROR_FIELD[e.code]) {
        const field = ERROR_FIELD[e.code];
        setError(field, { type: "server", message: e.message });
        //Błąd dotyczy typu - cofamy do kroku wyboru, bo tam jest jego przyczyna.
        if (field === "id_type") setStep("type");
        return;
      }
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
  });

  if (loading) {
    return (
      <div className={styles.root}>
        <FormStatus variant="info">Wczytywanie formularza zgłoszenia...</FormStatus>
      </div>
    );
  }

  if (loadError || !options) {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">{loadError ?? "Nie udało się wczytać formularza."}</FormStatus>
        <button type="button" className={styles.pageButton} onClick={onCancel}>
          <Icon name="caret-left" />
          Wróć do zadań
        </button>
      </div>
    );
  }

  /* Projekt bez menadżera - backend odrzuci każde zgłoszenie i już powiadomił
     administrację, więc pokazujemy sam komunikat zamiast kreatora. */
  if (!options.can_add) {
    return (
      <div className={styles.root}>
        <FormStatus variant="warning">
          Ten projekt nie ma jeszcze przydzielonego menadżera projektu, więc zgłoszenie nie
          miałoby komu trafić. Powiadomiliśmy o tym nasz zespół - prosimy o kontakt, jeśli
          sprawa jest pilna.
        </FormStatus>
        {options.offer_request_url && (
          <a className={styles.ctaLink} href={options.offer_request_url}>
            <Icon name="chat-circle-text" />
            Skontaktuj się z nami
          </a>
        )}
        <button type="button" className={styles.pageButton} onClick={onCancel}>
          <Icon name="caret-left" />
          Wróć do zadań
        </button>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <p className={styles.note}>
        Nowe zgłoszenie w projekcie <strong>{project.name}</strong> ({project.nr}).
      </p>

      <ol className={styles.steps}>
        <li className={step === "type" ? styles.stepCurrent : styles.stepDone}>
          <span className={styles.stepNo}>1</span> Typ zgłoszenia
        </li>
        <li className={step === "form" ? styles.stepCurrent : styles.stepNext}>
          <span className={styles.stepNo}>2</span> Szczegóły
        </li>
      </ol>

      {step === "type" && (
        <>
          {!hasAnyAvailableType && (
            <FormStatus variant="warning">
              W tym projekcie nie możesz obecnie zgłosić zadania - żaden z typów zgłoszeń nie
              jest dostępny. Skontaktuj się z nami, dobierzemy odpowiedni pakiet obsługi.
            </FormStatus>
          )}

          <div className={styles.typeGrid}>
            {options.types.map((type) => {
              const presentation = TYPE_PRESENTATION[type.id];
              return (
                <button
                  key={type.id}
                  type="button"
                  className={type.available ? styles.typeCard : styles.typeCardDisabled}
                  onClick={() => chooseType(type)}
                  disabled={!type.available}
                  aria-describedby={!type.available && type.reason ? `type-reason-${type.id}` : undefined}
                >
                  <span className={styles.typeCardIcon}>
                    <Icon name={presentation?.icon ?? "list-checks"} />
                  </span>
                  <span className={styles.typeCardTitle}>{type.name}</span>
                  {presentation?.description && (
                    <span className={styles.typeCardDesc}>{presentation.description}</span>
                  )}
                  {!type.available && type.reason && (
                    <span className={styles.typeCardReason} id={`type-reason-${type.id}`}>
                      <Icon name="info" />
                      {REASON_TEXT[type.reason]}
                    </span>
                  )}
                  {type.available && (
                    <span className={styles.typeCardGo}>
                      Wybierz
                      <Icon name="caret-right" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {errors.id_type && <p className={styles.typeError}>{errors.id_type.message}</p>}

          {/* Odesłanie do zapytania ofertowego - gdy nic nie jest dostępne albo gdy
              zwykłe zadanie jest zablokowane brakiem pakietu (najczęstszy przypadek). */}
          {options.offer_request_url &&
            (!hasAnyAvailableType ||
              options.types.some(
                (t) => t.id === TASK_TYPE_TASK && t.reason === "requires_package",
              )) && (
              <a className={styles.ctaLink} href={options.offer_request_url}>
                <Icon name="chat-circle-text" />
                {hasAnyAvailableType ? "Zapytaj o pakiet obsługi" : "Wyślij zapytanie ofertowe"}
              </a>
            )}

          <div className={styles.formActions}>
            <SubmitButton variant="link" type="button" onClick={onCancel}>
              Anuluj
            </SubmitButton>
          </div>
        </>
      )}

      {step === "form" && (
        <form className={styles.taskForm} onSubmit={onSubmit} noValidate>
          {/* Typ jest wybrany w kroku 1 i nie ma tu własnej kontrolki - trzymamy go
              w ukrytym polu, żeby wartość pozostała częścią stanu formularza. */}
          <input type="hidden" {...register("id_type")} />

          <div className={styles.chosenType}>
            <span className={styles.chosenTypeIcon}>
              <Icon name={TYPE_PRESENTATION[selectedTypeId]?.icon ?? "list-checks"} />
            </span>
            <span className={styles.chosenTypeText}>
              <strong>{selectedType?.name}</strong>
              {TYPE_PRESENTATION[selectedTypeId]?.description && (
                <span className={styles.chosenTypeDesc}>
                  {TYPE_PRESENTATION[selectedTypeId].description}
                </span>
              )}
            </span>
            <button type="button" className={styles.rowButton} onClick={() => setStep("type")}>
              <Icon name="arrow-counter-clockwise" />
              Zmień typ
            </button>
          </div>

          {needsService && (
            <Field
              label="Usługa"
              required
              error={errors.id_project_service?.message}
              hint="Wskaż usługę, której dotyczy zgłoszenie."
            >
              {/* Lista z WYBRANEGO typu, nie wspólna dla formularza - konfiguracja
                  wskazuje osobne pakiety dla zadania, wsparcia i gwarancji, więc
                  przy gwarancji widać wyłącznie usługi gwarancyjne. */}
              <Select {...register("id_project_service")}>
                <option value="">- wybierz usługę -</option>
                {(selectedType?.services ?? []).map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {[s.nr, s.type_name, s.package_name].filter(Boolean).join(" - ")}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {options.can_set_priority && (
            <fieldset className={styles.priorityFieldset}>
              <legend className={styles.priorityLegend}>Priorytet</legend>
              <p className={styles.priorityHint}>
                Możesz nadać priorytet, bo ten projekt jest objęty usługą priorytetową.
              </p>

              <div className={styles.priorityGrid}>
                {options.priorities.map((p) => (
                  <label key={p.id} className={styles.priorityCard}>
                    <input
                      type="radio"
                      className={styles.priorityRadio}
                      value={p.id}
                      {...register("id_priority")}
                    />
                    {PRIORITY_PRESENTATION[p.id] && (
                      <Icon
                        name={PRIORITY_PRESENTATION[p.id].icon}
                        className={styles.priorityCardIcon}
                      />
                    )}
                    <span className={styles.priorityCardBody}>
                      <span className={styles.priorityCardName}>{p.name}</span>
                      {PRIORITY_PRESENTATION[p.id]?.description && (
                        <span className={styles.priorityCardDesc}>
                          {PRIORITY_PRESENTATION[p.id].description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>

              {errors.id_priority && (
                <p className={styles.typeError}>{errors.id_priority.message}</p>
              )}
            </fieldset>
          )}

          <Field label="Nazwa zadania" required error={errors.name?.message}>
            <Input type="text" maxLength={500} {...register("name")} />
          </Field>

          <Field
            label="Opis"
            required
            error={errors.description?.message}
            hint="Opisz czego dotyczy zgłoszenie - im więcej szczegółów, tym szybciej je obsłużymy."
          >
            <Textarea rows={6} {...register("description")} />
          </Field>

          <FileUpload
          label="Załączniki"
          optional
          multiple
          value={files}
          onChange={setFiles}
          maxFiles={MAX_TASK_FILES}
          hint="Zrzuty ekranu, dokumenty - możesz wskazać kilka plików naraz."
        />

        {options.project_manager && (
            <p className={styles.assignedInfo}>
              <Icon name="user" />
              Zgłoszenie trafi do menadżera projektu:{" "}
              <strong>{options.project_manager.fullname}</strong>
            </p>
          )}

          {submitError && <FormStatus variant="error">{submitError}</FormStatus>}

          <div className={styles.formActions}>
            <SubmitButton variant="link" type="button" onClick={() => setStep("type")}>
              Wstecz
            </SubmitButton>
            <SubmitButton loading={isSubmitting}>Zgłoś zadanie</SubmitButton>
          </div>
        </form>
      )}
    </div>
  );
}
